import React from 'react';

/**
 * Decorative dot-trail strip rendered directly under a page's top header.
 *
 * Visual spec (Batch 6, A5): 30 px tall, full-width, SVG from
 * /public/brand/arp-dot-trail.svg laid out so the pattern repeats across
 * the viewport, flush at the top with 8 px of breathing space below. Not
 * focusable, ignored by screen readers.
 *
 * We render the SVG with inline-background-image styles rather than a CSS
 * `url(/brand/...)` because CRA's css-loader tries to resolve `url(...)`
 * paths as local modules and errors on absolute public paths.
 */
export function DotTrail({ topOffset = 64, inline = false }) {
  const style = inline ? {
    position: 'relative',
    width: '100%',
    height: '30px',
    pointerEvents: 'none',
    backgroundImage: 'url(/brand/arp-dot-trail.svg)',
    backgroundRepeat: 'repeat-x',
    backgroundSize: '390px 30px',
    backgroundPosition: 'left top',
    backgroundColor: '#ffffff',
  } : {
    position: 'fixed',
    top: `${topOffset}px`,
    left: 0,
    right: 0,
    height: '30px',
    width: '100%',
    pointerEvents: 'none',
    zIndex: 30,
    backgroundImage: 'url(/brand/arp-dot-trail.svg)',
    backgroundRepeat: 'repeat-x',
    backgroundSize: '390px 30px',
    backgroundPosition: 'left top',
    backgroundColor: '#ffffff',
  };
  return (
    <div
      className="theme-4a-dot-trail"
      aria-hidden="true"
      style={style}
    />
  );
}

/**
 * Decorative wave strip. Variant `fixed` (the default) is used on pages
 * that have the bottom menu bar: it sits flush above it. Variant `static`
 * is for pages without a bottom menu bar: it is rendered inline at the end
 * of the page content, directly above a page footer. `aboveBarHeight`
 * controls where the fixed wave sits (56 px for the shared Footer.js,
 * 64 px for Dashboard's custom nav).
 */
export function WaveTrail({ variant = 'fixed', aboveBarHeight = 56 }) {
  const className =
    variant === 'static' ? 'theme-4a-wave-trail--static' : 'theme-4a-wave-trail';
  const style = {
    backgroundImage: 'url(/brand/arp-wave-trail.svg)',
    backgroundRepeat: 'repeat-x',
    backgroundSize: '390px 58px',
    backgroundPosition: 'left bottom',
  };
  if (variant !== 'static') {
    style.position = 'fixed';
    style.left = 0;
    style.right = 0;
    style.bottom = `${aboveBarHeight}px`;
    style.height = '58px';
    style.width = '100%';
    style.pointerEvents = 'none';
    style.zIndex = 49;
    style.overflow = 'hidden';
  } else {
    style.width = '100%';
    style.height = '58px';
    style.pointerEvents = 'none';
    style.margin = 0;
    style.overflow = 'hidden';
  }
  return (
    <div
      className={className}
      aria-hidden="true"
      style={style}
    />
  );
}

/**
 * Auto-injects the dot-trail and wave strips at the top of <body> whenever
 * body.theme-4a is present (i.e. a page that opted in with useTheme4A()).
 * This lives inside App.js so individual pages only need `useTheme4A()` to
 * get the full look, without having to render the two strips themselves.
 *
 * We watch the body class list via MutationObserver so toggling at mount
 * and unmount is picked up without a React re-render path. The strips are
 * removed from the tree when the page unmounts the body class.
 */
export function Theme4AGlobalDecor() {
  const [enabled, setEnabled] = React.useState(
    typeof document !== 'undefined' && document.body.classList.contains('theme-4a')
  );
  // Dashboard sets its own thicker nav bar (h-16). All other in-scope pages
  // use the shared Footer.js (h-14). Pages override via data-theme-4a-bar.
  const [barHeight, setBarHeight] = React.useState(56);
  // When the current page passed `fixedDecor: false` to useTheme4A() we
  // skip the global dot-trail + wave injection entirely. The page is
  // responsible for rendering any inline decor it wants.
  const [fixedDecor, setFixedDecor] = React.useState(true);

  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const update = () => {
      const on = document.body.classList.contains('theme-4a');
      setEnabled(on);
      const barOverride = document.body.getAttribute('data-theme-4a-bar');
      setBarHeight(barOverride === '64' ? 64 : 56);
      const decorOverride = document.body.getAttribute('data-theme-4a-fixed-decor');
      setFixedDecor(decorOverride !== 'off');
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme-4a-bar', 'data-theme-4a-fixed-decor'] });
    return () => obs.disconnect();
  }, []);

  if (!enabled) return null;
  if (!fixedDecor) return null;
  return (
    <>
      <DotTrail topOffset={64} />
      <WaveTrail aboveBarHeight={barHeight} />
    </>
  );
}

const Theme4A = { DotTrail, WaveTrail, Theme4AGlobalDecor };
export default Theme4A;
